import axiosInstance from '$api/index';
import {ApiRoute} from '$constants/api';
import store from '$redux/store';
import {Patient} from '~/modules/frontdesk/Finance/PaymentList/hooks';
import {InsuranceClaimsOrderBy} from '~/modules/frontdesk/Finance/InsuranceClaims/constants';

export interface InsuranceClaim extends Record<string, unknown> {
  id: number;
  membershipNo: string;
  pre_authorisation_code: string;
  approved_amount: number;
  amount: number;
  patient_id: number;
  invoice_issued_at: string;
  outstandingAmount: number;
  status: string;
  insurerName: string;
  patientName: string;
}

export interface GetInsuranceClaimsResponseData {
  success: boolean;
  data: {
    insurance_claims: {
      id: number;
      membership_no: string;
      pre_authorisation_code: string | null;
      claimed_amount: number | null;
      approved_amount: number;
      status: string;
      insurer_name: string;
      insurance_id: number;
      invoice_number: string;
      outstanding_amount: number;
      patient_name: string;
      items: {
        id: number;
        description: string;
        notes: string | null;
        amount: number;
        category: string;
        item_name: string;
        ccsd_code: string;
      }[];
    }[];
    total_pages: number;
    total_records: number;
    all_claims_count: number;
    new_claims_count: number;
    paid_claims_count: number;
    rejected_claims_count: number;
    submitted_claims_count: number;
    pending_claims_count: number;
    outstanding_claims_count: number;
  };
  message: string;
}

export interface GetInsuranceClaimsParams {
  search?: string;
  sortBy?: InsuranceClaimsOrderBy | string;
  sortDirection?: 'asc' | 'desc';
  pageNumber?: number;
  perPage?: number;
  status?: string;
  patientId?: number;
}

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;

const mapApiInsuranceClaimToInsuranceClaim = (
  apiInsuranceClaim: GetInsuranceClaimsResponseData['data']['insurance_claims'][0]
): InsuranceClaim => {
  return {
    id: apiInsuranceClaim.id,
    membershipNo: apiInsuranceClaim.membership_no,
    outstandingAmount: apiInsuranceClaim.outstanding_amount,
    status: apiInsuranceClaim.status,
    insurerName: apiInsuranceClaim.insurer_name,
    patientName: apiInsuranceClaim.patient_name,
    pre_authorisation_code: apiInsuranceClaim.pre_authorisation_code || '',
    approved_amount: apiInsuranceClaim.approved_amount,
    amount: apiInsuranceClaim.claimed_amount || 0,
    patient_id: 0,
    invoice_issued_at: '',
  };
};

export async function getInsuranceClaims(
  params: GetInsuranceClaimsParams = {}
): Promise<{
  insuranceClaims: InsuranceClaim[];
  totalPages: number;
  totalRecords: number;
  message: string;
  success: boolean;
  all_claims_count: number;
  new_claims_count: number;
  pending_claims_count: number;
  submitted_claims_count: number;
  rejected_claims_count: number;
  paid_claims_count: number;
  outstading_claims_count: number;
  patient?: Patient;
}> {
  const {
    search,
    sortBy,
    sortDirection = 'asc',
    pageNumber = DEFAULT_PAGE_NUMBER,
    perPage = DEFAULT_PAGE_SIZE,
    status,
    patientId,
  } = params;

  const token = store.getState().auth.token;
  if (!token) {
    throw new Error('Authentication token is missing');
  }

  const response = await axiosInstance.get<GetInsuranceClaimsResponseData>(
    ApiRoute.InsuranceClaims,
    {
      params: {
        search,
        ...(sortBy && {sort_by: sortBy}),
        ...(sortBy && {sort_direction: sortDirection}),
        page_no: pageNumber,
        per_page: perPage,
        ...(status && {status}),
        ...(patientId && {patient_id: patientId}),
      },
      headers: {
        Authorization: token,
      },
    }
  );

  if (!response.data.success) {
    throw new Error(
      response.data.message || 'Failed to fetch insurance claims'
    );
  }

  const responseData = response.data.data;
  const responseInsuranceClaims = responseData?.insurance_claims || [];

  const allClaimsCount =
    responseData.all_claims_count || responseData.total_records || 0;

  const newClaimCount = responseData.new_claims_count || 0;
  const pendingCount = responseData.pending_claims_count || 0;
  const submittedCount = responseData.submitted_claims_count || 0;
  const rejectedCount = responseData.rejected_claims_count || 0;
  const paidCount = responseData.paid_claims_count || 0;
  const outstandingCount = responseData.outstanding_claims_count || 0;

  return {
    success: response.data.success,
    message: response.data.message,
    insuranceClaims: responseInsuranceClaims.map(
      mapApiInsuranceClaimToInsuranceClaim
    ),
    totalPages: responseData.total_pages,
    totalRecords: responseData.total_records,
    all_claims_count: allClaimsCount,
    new_claims_count: newClaimCount,
    pending_claims_count: pendingCount,
    submitted_claims_count: submittedCount,
    rejected_claims_count: rejectedCount,
    paid_claims_count: paidCount,
    outstading_claims_count: outstandingCount,
  };
}
